import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { PropertySettings } from '@vuokramani/shared';
import { Section } from '../../components/page.tsx';
import { Alert, Button, Notice, Panel } from '../../components/ui.tsx';
import { errorKey } from '../../lib/api.ts';
import { useUpdatePropertySettings } from '../../lib/properties.ts';
import { useCurrentProperty } from './PropertyLayout.tsx';
import st from './settings.module.css';

const KEYS: Array<keyof PropertySettings> = [
  'tenantInspections',
  'tenantPaymentReports',
  'tenantInvoices',
  'tenantMessages',
  'maintenanceShared',
];

/**
 * Asuntokohtaiset asetukset: mitä palveluun kutsuttu vuokralainen näkee ja tekee.
 * Muutokset kirjautuvat lokiin todisteeksi.
 */
export function PropertySettingsTab() {
  const { t } = useTranslation();
  const p = useCurrentProperty();
  const save = useUpdatePropertySettings(p.id);
  const [values, setValues] = useState<PropertySettings>(p.settings);
  const [saved, setSaved] = useState(false);
  const changed = KEYS.some((k) => values[k] !== p.settings[k]);
  const readOnly = Boolean(p.archivedAt);

  function submit(event: FormEvent) {
    event.preventDefault();
    setSaved(false);
    save.mutate(values, { onSuccess: () => setSaved(true) });
  }

  return (
    <Section title={t('settings.title')} note={t('settings.note')}>
      <Panel>
        <form onSubmit={submit} className={st.form}>
          {KEYS.map((key) => (
            <label key={key} className={st.row}>
              <input
                type="checkbox"
                checked={values[key]}
                disabled={readOnly}
                onChange={(e) => {
                  setSaved(false);
                  setValues({ ...values, [key]: e.target.checked });
                }}
              />
              <span className={st.text}>
                <strong>{t(`settings.${settingKey(key)}.title`)}</strong>
                <span>
                  {values[key]
                    ? t(`settings.${settingKey(key)}.on`)
                    : t(`settings.${settingKey(key)}.off`)}
                </span>
              </span>
            </label>
          ))}
          {save.isError && <Alert>{t(errorKey(save.error))}</Alert>}
          {saved && !changed && <Notice>{t('settings.saved')}</Notice>}
          {!readOnly && (
            <div>
              <Button type="submit" variant="primary" disabled={!changed || save.isPending}>
                {t('common.save')}
              </Button>
            </div>
          )}
        </form>
      </Panel>
    </Section>
  );
}

/** Asetuksen avain käännöksissä ja lokissa (tenantInvoices → invoices). */
function settingKey(key: keyof PropertySettings): string {
  switch (key) {
    case 'tenantInspections':
      return 'inspections';
    case 'tenantPaymentReports':
      return 'paymentReports';
    case 'tenantInvoices':
      return 'invoices';
    case 'tenantMessages':
      return 'messages';
    case 'maintenanceShared':
      return 'maintenanceShared';
  }
}
